import { useState } from 'react';
import { Box, Chip } from '@mui/material';
import {
  AutocompleteInput, Datagrid, DateField, DateInput, FilterButton, FunctionField, List, ReferenceField, ReferenceInput,
  SelectArrayInput, SelectInput, TextField, TopToolbar,
} from 'react-admin';
import ListPagination from '../../components/ListPagination';
import { __t } from '../../i18n/t';
import { InternalOfferRow } from '../../models/attendantGoalOffer';
import { AttendantGoalType } from '../../models/attendantGoalType';
import { filterToQuery } from '../../utils';
import FunnelCard from './FunnelCard';
import InternalGate from './InternalGate';
import SendOfferButton from './SendOfferButton';
import { DATE_TIME, rolloutChoices, rolloutColor, rolloutLabel, statusChoices, statusColor, statusLabel } from './labels';

// Função (não constante de módulo): `choices` traduz no render, o idioma troca sem reload.
const buildFilters = () => [
  <SelectArrayInput key="status" source="status" label="Status" choices={statusChoices()} sx={{ minWidth: 240 }} alwaysOn />,
  <ReferenceInput key="chainId" source="chainId" reference="chains" sort={{ field: 'name', order: 'ASC' }} perPage={25} alwaysOn>
    <AutocompleteInput label="Rede" optionText="name" filterToQuery={filterToQuery} sx={{ minWidth: 240 }} />
  </ReferenceInput>,
  <SelectInput key="rollout" source="rollout" label="Lançamento" choices={rolloutChoices()} sx={{ minWidth: 180 }} alwaysOn />,
  <DateInput key="createdFrom" source="createdFrom" label="Criado de" sx={{ width: 170 }} alwaysOn />,
  <DateInput key="createdTo" source="createdTo" label="Criado até" sx={{ width: 170 }} alwaysOn />,
  <SelectInput
    key="continueRequested"
    source="continueRequested"
    label="Pediu para continuar"
    choices={[{ id: true, name: __t('Sim') }, { id: false, name: __t('Não') }]}
    sx={{ minWidth: 200 }}
    alwaysOn
  />,
];

// `source` deixa o cabeçalho ordenável; todas estas colunas estão no OFFER_SORT do backend.
const dateTime = { showTime: true, locales: 'pt-BR', options: DATE_TIME, emptyText: '—', sx: { whiteSpace: 'nowrap' } } as const;

const ListActions = ({ onSent }: { onSent: () => void }) => (
  <TopToolbar>
    <FilterButton />
    <SendOfferButton onSent={onSent} />
  </TopToolbar>
);

const AttendantGoalOfferList = () => {
  // O funil não é do react-admin (useDashboardSection): o refresh da lista não o alcança. Depois de um envio a key refaz a busca.
  const [funnelKey, setFunnelKey] = useState(0);
  return (
    <InternalGate>
      <List
        title="Convites de Meta de Frentista"
        perPage={25}
        pagination={<ListPagination />}
        exporter={false}
        empty={false}
        actions={<ListActions onSent={() => setFunnelKey(k => k + 1)} />}
        filters={buildFilters()}
        sort={{ field: 'createdAt', order: 'DESC' }}
      >
        <>
          <FunnelCard key={funnelKey} />
          {/* RaLayout-root é fit-content: com 10 colunas, a tabela rola aqui dentro em vez de esticar a página. */}
          <Box sx={{ width: 0, minWidth: '100%', overflowX: 'auto' }}>
            <Datagrid bulkActionButtons={false} rowClick="show">
              <ReferenceField source="chainId" reference="chains" label="Rede" link="show" sortable={false}>
                <TextField source="name" />
              </ReferenceField>
              <FunctionField<InternalOfferRow>
                label="Lançamento"
                sortable={false}
                render={r => (r.rollout
                  ? <Chip size="small" variant="outlined" color={rolloutColor(r.rollout)} label={rolloutLabel(r.rollout)} />
                  : '—')}
              />
              <FunctionField<InternalOfferRow>
                source="status"
                label="Status"
                render={r => <Chip size="small" color={statusColor(r.status)} label={statusLabel(r.status, r.endedReason)} />}
              />
              <DateField source="createdAt" label="Criado em" {...dateTime} />
              <DateField source="firstSeenAt" label="Visto em" {...dateTime} />
              <DateField source="activatedAt" label="Ativado em" {...dateTime} />
              <DateField source="trialEndsAt" label="Fim da degustação" {...dateTime} />
              <FunctionField<InternalOfferRow>
                label="Postos com gatilho"
                sortable={false}
                render={r => __t('%{com} de %{total}', { com: r.stationsWithTrigger ?? 0, total: r.stationsAnalyzed ?? 0 })}
              />
              <FunctionField<InternalOfferRow>
                label="Tipos de meta"
                sortable={false}
                sx={{ whiteSpace: 'nowrap' }}
                render={r => (r.targetTypes?.length ? r.targetTypes.map(t => AttendantGoalType.translateType(t as AttendantGoalType)).join(' / ') : '—')}
              />
              <DateField source="continueRequestedAt" label="Pediu para continuar" {...dateTime} />
            </Datagrid>
          </Box>
        </>
      </List>
    </InternalGate>
  );
};

export default AttendantGoalOfferList;
